import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Zap, Terminal, Image as ImageIcon, X, Layout, Code, Copy, Check, Sparkles, Download, Link, Palette, Type, Focus, ImagePlus, Globe, Layers, Eye, BarChart3, Instagram, Search, MessageSquare, Activity } from 'lucide-react';
import WizardInput from './components/WizardInput';
import PageSelector from './components/PageSelector';
import MetaAdsDashboard from './components/MetaAdsDashboard';
import SocialPosting from './components/SocialPosting';
import SEODashboard from './components/SEODashboard';
import WhatsAppDashboard from './components/WhatsAppDashboard';
import AnalyticsDashboard from './components/AnalyticsDashboard';

const App = () => {
  const [appMode, setAppMode] = useState('website');
  const [editedImage, setEditedImage] = useState(null);

  const [input, setInput] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [output, setOutput] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const [viewState, setViewState] = useState('input');
  const [selectedPages, setSelectedPages] = useState([]);
  const [scanResult, setScanResult] = useState(null);
  const [generationMode, setGenerationMode] = useState('prompt');

  const [cloneColorPalette, setCloneColorPalette] = useState('Extract from URL');
  const [cloneTypography, setCloneTypography] = useState('Extract from URL');
  const [extractionScope, setExtractionScope] = useState('Full Page');
  const [techStack, setTechStack] = useState('nextjs');

  const [showMetaAds, setShowMetaAds] = useState(false);
  const [showSocialPosting, setShowSocialPosting] = useState(false);
  const [showSEO, setShowSEO] = useState(false);
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [liveWizardData, setLiveWizardData] = useState(null);


  // ✨ RESTORED: Smart Detection for URL vs Image Uploads
  const isUrlInput = /^(https?:\/\/)?([\w.-]+)\.[a-z]{2,6}(\/.*)?$/i.test(input.trim());
  const isImageUpload = selectedFile !== null;
  const isCloneMode = isUrlInput || isImageUpload;

  useEffect(() => {
    const handlePaste = (e) => {
      const items = e.clipboardData.items;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const file = items[i].getAsFile();
          setSelectedFile(file);
          setImagePreview(URL.createObjectURL(file));
          break;
        }
      }
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, []);

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };

  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    const files = e.dataTransfer.files;
    if (files && files.length > 0 && files[0].type.startsWith('image/')) {
      setSelectedFile(files[0]);
      setImagePreview(URL.createObjectURL(files[0]));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const clearImage = () => {
    setSelectedFile(null);
    setImagePreview(null);
    setScanResult(null);
    setViewState('input');
  };

  const resetFlow = () => {
    setScanResult(null);
    setOutput(null);
    setViewState('input');
    setCopied(false);
    setShowWizard(false);
    setShowSocialPosting(false);
    setShowMetaAds(false);
    setShowSEO(false);
    setShowWhatsApp(false);
    setShowAnalytics(false);
    setLiveWizardData(null);
    setSelectedPages([]);
    setCloneColorPalette('Extract from URL');
    setCloneTypography('Extract from URL');
    setExtractionScope('Full Page');
    setTechStack('nextjs');
    setEditedImage(null);
  };

  const handleCopy = () => {
    if (output) {
      navigator.clipboard.writeText(output);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    if (!output) return;
    const ext = techStack === 'nextjs' ? 'tsx' : 'html';
    const blob = new Blob([output], { type: generationMode === 'code' ? `text/${ext}` : 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sentinel_${generationMode}_${Date.now()}.${generationMode === 'code' ? ext : 'txt'}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImageEdit = async () => {
    if (!selectedFile) {
      setOutput("⚠️ Please upload a reference image first!");
      setViewState('result');
      return;
    }
    if (!input.trim()) {
      setOutput("⚠️ Please describe what you want to change (e.g., 'remove the air conditioner').");
      setViewState('result');
      return;
    }

    setViewState('result');
    setLoading(true);
    setEditedImage(null);
    setOutput(null);

    try {
      const formData = new FormData();
      formData.append('prompt', input);
      formData.append('image', selectedFile);

      const API_URL = process.env.REACT_APP_API_URL || 'https://rnd.kalptree.xyz/api';
      const response = await fetch(`${API_URL}/edit-image`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (data.status === 'success') {
        setEditedImage(data.image_url);
        setOutput(`✅ Action Complete: "${input}"`);
      } else {
        setOutput(`⚠️ Error from AI Editor: ${data.message}`);
      }
    } catch (error) {
      console.error("Error:", error);
      setOutput("⚠️ Backend Connection Failed. Is the Python server running?");
    }
    setLoading(false);
  };

  const handleWizardComplete = (wizardResult) => {
    setShowWizard(false);
    const generatedPrompt = typeof wizardResult === 'object' ? wizardResult.prompt : wizardResult;
    const refUrl = typeof wizardResult === 'object' ? wizardResult.referenceUrl : null;

    setInput(refUrl || generatedPrompt);

    if (refUrl && refUrl.trim() !== '') {
      handleUrlClone(refUrl, generatedPrompt);
    } else {
      const chosenType = liveWizardData?.formData?.projectType || 'Full Website';
      if (chosenType === 'Full Website') {
        setViewState('pages');
      } else {
        setSelectedPages([chosenType]);
        if (selectedFile) handleScan();
        else setViewState('templates');
      }
    }
  };

  const handlePagesConfirmed = (pages) => {
    setSelectedPages(pages);
    if (selectedFile) handleScan();
    else setViewState('templates');
  };

  // 🚀 URL & IMAGE CLONE HANDLER
  const handleUrlClone = async (customUrl = null, customWizardData = null) => {
    setViewState('result');
    setLoading(true);

    const actualUrl = typeof customUrl === 'string' ? customUrl : input.trim();
    const actualWizardData = typeof customWizardData === 'string' ? customWizardData : null;
    const targetScope = liveWizardData?.formData?.projectType || 'Website';

    try {
      const formData = new FormData();
      formData.append('target_url', actualUrl);
      formData.append('project_type', targetScope);
      formData.append('color_override', cloneColorPalette);
      formData.append('typography_override', cloneTypography);
      formData.append('extraction_scope', extractionScope);
      formData.append('tech_stack', techStack);
      if (actualWizardData) formData.append('wizard_data', actualWizardData);
      if (selectedFile) formData.append('reference_image', selectedFile);

      const API_URL = process.env.REACT_APP_API_URL || 'https://rnd.kalptree.xyz/api';
      const response = await fetch(`${API_URL}/deep-scan-url`, { method: 'POST', body: formData });
      const data = await response.json();

      if (generationMode === 'prompt') {
        setOutput(data.engineered_prompt);
      } else {
        const extension = techStack === 'nextjs' ? '.TSX FORMAT' : 'RAW HTML/CSS/JS';
        setOutput(data.engineered_prompt + `\n\nCRITICAL COMPILER INSTRUCTION: OUTPUT ONLY ${extension}. DO NOT INCLUDE MARKDOWN EXPLANATIONS.`);
      }

    } catch (error) {
      console.error(error);
      setOutput("⚠️ Error running deep scan. Make sure your Python backend is running!");
    }
    setLoading(false);
  };

  const handleScan = async () => {
    setLoading(true);
    setScanResult(null);
    setOutput(null);
    const formData = new FormData();
    formData.append('user_text', input);
    if (selectedFile) formData.append('reference_image', selectedFile);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://rnd.kalptree.xyz/api';
      const response = await fetch(`${API_URL}/scan-options`, { method: 'POST', body: formData });
      const data = await response.json();
      if (data.status === 'success') { setScanResult(data.data); setViewState('selection'); }
      else setOutput("Error scanning options.");
    } catch (error) { setOutput("Error connecting to API."); }
    setLoading(false);
  };

  const handleGenerateFinal = async (styleId) => {
    setLoading(true);
    setCopied(false);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://rnd.kalptree.xyz/api';
      console.log("🚀 Sending request to:", `${API_URL}/generate-selected`);

      const response = await fetch(`${API_URL}/generate-selected`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_text: input || "General Website",
          style_id: styleId,
          colors: scanResult ? scanResult.detected_colors : "Auto-Detect",
          mode: generationMode,
          pages: selectedPages
        }),
      });

      console.log("📡 Response Status:", response.status);
      const data = await response.json();

      if (data.engineered_prompt) {
        setOutput(data.engineered_prompt);
      } else {
        setOutput(`⚠️ Backend replied, but prompt was empty. Data: ${JSON.stringify(data)}`);
      }
      setViewState('result'); // Force the screen to change

    } catch (error) {
      console.error("💥 Network/Fetch Error:", error);
      setOutput(`⚠️ Network Error: Could not connect to Python backend.\n\nMake sure your FastAPI server is running on https://rnd.kalptree.xyz/api.\n\nError details: ${error.message}`);
      setViewState('result'); // Force the screen to change
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white p-6 md:p-10 font-sans">
      <div className="max-w-6xl mx-auto">

        <header className="mb-8 flex items-center justify-between border-b border-gray-800 pb-6">
          <div className="flex items-center space-x-3">
            <Shield className="w-8 h-8 text-blue-500" />
            <div>
              <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">
                Sentinel-Prompt
              </h1>
              <p className="text-gray-500 text-sm">Vision-Augmented Guardrails</p>
            </div>
          </div>
          {(viewState !== 'input' || showMetaAds || showSocialPosting || showSEO || showWhatsApp || showAnalytics) && (
            <button onClick={resetFlow} className="text-sm text-gray-400 hover:text-white underline transition-colors">
              {showMetaAds || showSocialPosting || showSEO || showWhatsApp || showAnalytics ? '← Back to Dashboard' : 'Start Over'}
            </button>
          )}
        </header>

        {/* Global Full-Width Navigation Bar */}
        <div className="mb-8 flex flex-wrap items-center bg-gray-900/90 backdrop-blur-md p-1.5 rounded-2xl border border-gray-800 shadow-xl gap-1.5 overflow-x-auto">
          <button
            onClick={() => { setAppMode('website'); setShowMetaAds(false); setShowSocialPosting(false); setShowSEO(false); setShowWhatsApp(false); setShowAnalytics(false); }}
            className={`px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-all ${appMode === 'website' && !showMetaAds && !showSocialPosting && !showSEO && !showWhatsApp && !showAnalytics ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'}`}
          >
            <Globe size={16} /> Build UI
          </button>
          <button
            onClick={() => { setAppMode('editor'); setShowMetaAds(false); setShowSocialPosting(false); setShowSEO(false); setShowWhatsApp(false); setShowAnalytics(false); }}
            className={`px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-all ${appMode === 'editor' && !showMetaAds && !showSocialPosting && !showSEO && !showWhatsApp && !showAnalytics ? 'bg-purple-600 text-white shadow-lg' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'}`}
          >
            <ImagePlus size={16} /> AI Image Editor
          </button>
          <button
            onClick={() => { setShowMetaAds(true); setShowSocialPosting(false); setShowSEO(false); setShowWhatsApp(false); setShowAnalytics(false); }}
            className={`px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-all ${showMetaAds ? 'bg-orange-600 text-white shadow-lg' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'}`}
          >
            <BarChart3 size={16} /> Meta Ads
          </button>
          <button
            onClick={() => { setShowSocialPosting(true); setShowMetaAds(false); setShowSEO(false); setShowWhatsApp(false); setShowAnalytics(false); }}
            className={`px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-all ${showSocialPosting ? 'bg-pink-600 text-white shadow-lg' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'}`}
          >
            <Instagram size={16} /> Social Post
          </button>
          <button
            onClick={() => { setShowSEO(true); setShowMetaAds(false); setShowSocialPosting(false); setShowWhatsApp(false); setShowAnalytics(false); }}
            className={`px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-all ${showSEO ? 'bg-teal-600 text-white shadow-lg' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'}`}
          >
            <Search size={16} /> SEO Audit
          </button>
          <button
            onClick={() => { setShowWhatsApp(true); setShowMetaAds(false); setShowSocialPosting(false); setShowSEO(false); setShowAnalytics(false); }}
            className={`px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-all ${showWhatsApp ? 'bg-emerald-600 text-white shadow-lg' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'}`}
          >
            <MessageSquare size={16} /> WhatsApp
          </button>
          <button
            onClick={() => { setShowAnalytics(true); setShowMetaAds(false); setShowSocialPosting(false); setShowSEO(false); setShowWhatsApp(false); }}
            className={`px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-all ${showAnalytics ? 'bg-indigo-600 text-white shadow-lg' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'}`}
          >
            <Activity size={16} /> Web Analytics
          </button>
        </div>

        {showWhatsApp ? (
          <WhatsAppDashboard />
        ) : showMetaAds ? (
          <MetaAdsDashboard />
        ) : showSocialPosting ? (
          <SocialPosting />
        ) : showSEO ? (
          <SEODashboard />
        ) : showAnalytics ? (
          <AnalyticsDashboard />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

            <div className="space-y-6">
              {viewState === 'input' && (
                <>
                  {showWizard ? (
                    <WizardInput onComplete={handleWizardComplete} onCancel={() => { setShowWizard(false); setLiveWizardData(null); }} onLiveUpdate={setLiveWizardData} />
                  ) : (
                    <>
                      <div className="bg-gray-900 p-1 rounded-xl border border-gray-800 shadow-lg relative group" onDragOver={handleDragOver} onDrop={handleDrop}>
                        <div className={`relative w-full h-48 rounded-lg border-2 border-dashed ${imagePreview ? (appMode === 'website' ? 'border-blue-500/50' : 'border-purple-500/50') : 'border-gray-700'} flex flex-col items-center justify-center transition-all hover:bg-gray-800`}>
                          {imagePreview ? (
                            <>
                              <img src={imagePreview} alt="Preview" className="h-full w-full object-cover rounded-lg opacity-50" />
                              <button onClick={clearImage} className="absolute inset-0 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 font-medium z-10 bg-black/40 rounded-lg transition-opacity"><X className="w-5 h-5 mr-2" /> Remove</button>
                            </>
                          ) : (
                            <>
                              <ImageIcon className="w-8 h-8 text-gray-500 mb-2" />
                              <span className="text-gray-500 text-sm font-medium">Drag & Drop Image or Paste (Ctrl+V)</span>
                            </>
                          )}
                          <input type="file" onChange={handleFileChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" accept="image/*" />
                        </div>
                      </div>

                      <div className="bg-gray-900 p-6 rounded-xl border border-gray-800 shadow-lg relative">
                        <div className="flex justify-between items-center mb-4">
                          <h2 className="flex items-center text-lg font-semibold text-gray-200">
                            <Zap className={`w-4 h-4 mr-2 ${appMode === 'website' ? 'text-yellow-500' : 'text-purple-400'}`} />
                            {appMode === 'website' ? 'Project Description' : 'AI Edit Prompt'}
                          </h2>
                          {appMode === 'website' && (
                            <button onClick={() => setShowWizard(true)} className="text-xs flex items-center gap-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white px-3 py-1 rounded-full transition shadow-lg">
                              <Sparkles size={12} /> Wizard
                            </button>
                          )}
                        </div>

                        <div className="relative">
                          <textarea
                            className={`w-full h-32 bg-gray-950 border ${isCloneMode && appMode === 'website' ? 'border-green-500/50 focus:ring-green-500/50' : 'border-gray-800 focus:ring-blue-500'} rounded-lg p-4 text-gray-300 focus:ring-1 outline-none resize-none text-sm transition-all placeholder-gray-600`}
                            placeholder={appMode === 'website' ? "E.g., 'Gym dashboard', paste a website link, or drop an image!" : "E.g., 'Remove the air conditioner from the wall' or 'Change the sofa to green leather'"}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                          />
                          <AnimatePresence>
                            {isCloneMode && appMode === 'website' && (
                              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="absolute top-3 right-3 bg-green-500/20 text-green-400 text-xs px-2 py-1 rounded-md border border-green-500/30 flex items-center gap-1.5 backdrop-blur-sm">
                                {isImageUpload && !isUrlInput ? <Eye size={12} /> : <Link size={12} />}
                                {isImageUpload && !isUrlInput ? "Vision Target Detected" : "Target URL Detected"}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>

                        {appMode === 'website' && (
                          <div className="flex gap-4 mt-4">
                            <button onClick={() => setGenerationMode('prompt')} className={`flex-1 py-2 rounded border flex items-center justify-center gap-2 transition-colors ${generationMode === 'prompt' ? 'bg-blue-600 border-blue-500 text-white' : 'border-gray-700 text-gray-500 hover:border-gray-600'}`}><Layout size={16} /> Prompt</button>
                            <button onClick={() => setGenerationMode('code')} className={`flex-1 py-2 rounded border flex items-center justify-center gap-2 transition-colors ${generationMode === 'code' ? 'bg-green-600 border-green-500 text-white' : 'border-gray-700 text-gray-500 hover:border-gray-600'}`}><Code size={16} /> Code</button>
                          </div>
                        )}

                        <AnimatePresence>
                          {isCloneMode && appMode === 'website' && (
                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 mb-2">
                              <div className="flex flex-col gap-1.5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1"><Focus size={12} className="text-yellow-400" /> Scope</label>
                                <select value={extractionScope} onChange={(e) => setExtractionScope(e.target.value)} className="bg-gray-950 border border-gray-700 rounded-lg p-2 text-xs text-gray-300 focus:border-yellow-500 outline-none">
                                  <option value="Full Page">Full Page</option><option value="Header & Navigation">Header</option><option value="Pricing Table">Pricing</option><option value="Footer Only">Footer</option>
                                </select>
                              </div>
                              <div className="flex flex-col gap-1.5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1"><Layers size={12} className="text-pink-400" /> Stack</label>
                                <select value={techStack} onChange={(e) => setTechStack(e.target.value)} className="bg-gray-950 border border-gray-700 rounded-lg p-2 text-xs text-gray-300 focus:border-pink-500 outline-none">
                                  <option value="nextjs">Next.js/React</option><option value="html">HTML/CSS/JS</option>
                                </select>
                              </div>
                              <div className="flex flex-col gap-1.5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1"><Palette size={12} className="text-blue-400" /> Colors</label>
                                <select value={cloneColorPalette} onChange={(e) => setCloneColorPalette(e.target.value)} className="bg-gray-950 border border-gray-700 rounded-lg p-2 text-xs text-gray-300 focus:border-blue-500 outline-none">
                                  <option value="Extract from URL">Extract</option><option value="Minimalist Mono">Mono</option><option value="Cyberpunk Neon">Cyberpunk</option>
                                </select>
                              </div>
                              <div className="flex flex-col gap-1.5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1"><Type size={12} className="text-purple-400" /> Font</label>
                                <select value={cloneTypography} onChange={(e) => setCloneTypography(e.target.value)} className="bg-gray-950 border border-gray-700 rounded-lg p-2 text-xs text-gray-300 focus:border-purple-500 outline-none">
                                  <option value="Extract from URL">Extract</option><option value="Modern Sans-Serif">Sans</option><option value="Tech Monospace">Mono</option>
                                </select>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        <button
                          onClick={appMode === 'editor' ? handleImageEdit : (isCloneMode ? () => handleUrlClone() : () => setViewState('pages'))}
                          disabled={loading || (!input && !selectedFile)}
                          className={`mt-4 w-full font-bold py-3 px-4 rounded-lg flex items-center justify-center transition-all ${loading ? 'bg-gray-800 text-gray-500 cursor-not-allowed' :
                              appMode === 'editor' ? 'bg-purple-600 hover:bg-purple-500 text-white shadow-lg shadow-purple-900/20' :
                                isCloneMode ? 'bg-green-600 hover:bg-green-500 text-white shadow-lg shadow-green-900/20' :
                                  'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-900/20'
                            }`}
                        >
                          {appMode === 'editor' ? (
                            <><ImagePlus size={18} className="mr-2" /> Generate Image Edit →</>
                          ) : isCloneMode ? (
                            <><Sparkles size={18} className="mr-2" /> Generate UI Prompt →</>
                          ) : (
                            'Next: Select Pages →'
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}

              {viewState === 'pages' && <PageSelector onConfirm={handlePagesConfirmed} onBack={() => setViewState('input')} />}

              {viewState === 'templates' && (
                <div className="bg-gray-900 p-6 rounded-xl border border-gray-800 shadow-lg animate-in fade-in slide-in-from-bottom-4">
                  <h2 className="text-xl font-bold mb-4">Choose a Design System</h2>
                  <div className="grid gap-4">
                    <button onClick={() => handleGenerateFinal('minimalist')} className="p-5 bg-gray-950 border border-gray-800 rounded hover:border-blue-500 text-left">Minimalist & Elegant</button>
                    <button onClick={() => handleGenerateFinal('cyberpunk')} className="p-5 bg-gray-950 border border-gray-800 rounded hover:border-blue-500 text-left">Dark Cyberpunk</button>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-gray-900 p-6 rounded-xl border border-gray-800 shadow-lg flex flex-col h-full min-h-[500px]">
              <div className="flex items-center justify-between mb-4">
                <h2 className="flex items-center text-lg font-semibold text-gray-200">
                  <Terminal className="w-4 h-4 mr-2 text-green-500" />
                  {viewState === 'result' ? (appMode === 'editor' ? 'AI Edit Result' : (generationMode === 'code' ? 'Compiler Output' : 'Engineered Prompt')) : 'System Status'}
                </h2>

                {output && appMode === 'website' && (
                  <div className="flex gap-3 items-center">
                    <button
                      onClick={handleDownload}
                      className="text-xs px-3 py-1.5 rounded flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white transition-all"
                    >
                      <Download size={14} /> Download File
                    </button>
                    <button
                      onClick={handleCopy}
                      className={`text-xs px-3 py-1.5 rounded flex items-center gap-2 transition-all ${copied ? 'bg-green-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        }`}
                    >
                      {copied ? <Check size={14} /> : <Copy size={14} />}
                      {copied ? "Copied!" : "Copy Output"}
                    </button>
                  </div>
                )}
              </div>

              <div className={`flex-1 bg-black rounded-lg overflow-hidden border border-gray-800 shadow-inner relative flex justify-center items-start pt-4 pb-4`}>

                {editedImage ? (
                  <div className="w-full h-full flex flex-col items-center justify-center p-4">
                    <img src={editedImage} alt="AI Generated Edit" className="max-h-full max-w-full rounded shadow-2xl border border-gray-700" />
                  </div>
                ) : output && viewState === 'result' ? (
                  <div className="w-full h-full p-6 overflow-y-auto bg-gray-950 text-green-400 font-mono text-sm leading-relaxed whitespace-pre-wrap custom-scrollbar text-left">{output}</div>
                ) : (showWizard && liveWizardData && liveWizardData.formData?.businessName?.length > 0) ? (

                  <div className="w-full h-full flex flex-col bg-[#0A0A0A]">
                    <div className="bg-[#111111] px-4 py-3 border-b border-gray-800 flex items-center gap-2">
                      <div className="flex gap-2">
                        <div className="w-3 h-3 rounded-full bg-[#FF5F56]"></div>
                        <div className="w-3 h-3 rounded-full bg-[#FFBD2E]"></div>
                        <div className="w-3 h-3 rounded-full bg-[#27C93F]"></div>
                      </div>
                      <span className="ml-2 flex-1 text-center pr-10 text-[11px] font-bold text-gray-500 font-mono tracking-widest uppercase flex items-center justify-center gap-2">
                        <Terminal size={12} /> compiler.sh
                      </span>
                    </div>

                    <div className="p-6 font-mono text-sm leading-loose overflow-y-auto custom-scrollbar text-gray-300 w-full text-left flex-1">
                      <span className="text-[#569CD6]">Compile UI components for</span>{' '}
                      <span className="text-[#4EC9B0]">'{liveWizardData.formData.businessName}'</span>.

                      {liveWizardData.step >= 1 && (
                        <div className="mt-4">
                          <span className="text-[#C586C0] font-bold">- **Target:**</span> <span className="text-[#4EC9B0]">{liveWizardData.formData.projectType}</span>.
                        </div>
                      )}

                      {liveWizardData.step >= 2 && (
                        <div className="mt-2">
                          <span className="text-[#C586C0] font-bold">- Stack:</span>{' '}
                          <span className="text-[#CE9178]">{liveWizardData.formData.techStack === 'html' ? 'HTML/CSS/JS' : 'Next.js/React'}</span>
                        </div>
                      )}

                      {liveWizardData.step >= 3 && (
                        <div className="mt-2">
                          <span className="text-[#C586C0] font-bold">- Industry:</span>{' '}
                          <span className="text-[#CE9178]">{liveWizardData.formData.industry || '[Pending...]'}</span>
                        </div>
                      )}

                      {liveWizardData.step >= 4 && (
                        <div className="mt-2">
                          <span className="text-[#C586C0] font-bold">- Modules:</span>{' '}
                          <span className="text-[#4EC9B0]">{liveWizardData.formData.focusAreas.length > 0 ? liveWizardData.formData.focusAreas.join(', ') : '[Pending...]'}</span>
                        </div>
                      )}

                      {liveWizardData.formData.referenceUrl && (
                        <div className="mt-4 p-3 bg-blue-900/20 border border-blue-500/30 rounded text-blue-300">
                          <span className="text-[#9CDCFE] font-bold">▶ COMPONENT INJECTION:</span> Extracting styling rules from {liveWizardData.formData.referenceUrl}.
                        </div>
                      )}

                      <div className="mt-4 animate-pulse inline-block w-2.5 h-4 bg-gray-500 align-middle"></div>
                    </div>
                  </div>

                ) : (
                  <div className="h-full w-full flex flex-col items-center justify-center text-gray-700 space-y-2 p-5">
                    {loading ? (
                      <><div className="w-2 h-2 bg-blue-500 rounded-full animate-ping" /><p className="text-xs uppercase tracking-widest">{appMode === 'editor' ? 'Processing Image via AI...' : 'Compiling Code...'}</p></>
                    ) : (
                      <p className="text-xs uppercase tracking-widest">Waiting for Input</p>
                    )}
                  </div>
                )}
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default App;